import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import Button from './ui/Button';
import Modal from './ui/Modal';
import SignatureSetupModal from './SignatureSetupModal';
import SignPasswordModal from './SignPasswordModal';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/dateFormat';

const ChangeSignaturePopup = ({ onDone }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [showNewSigSetup, setShowNewSigSetup] = useState(false);
  const [newSigData, setNewSigData] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [refuseMode, setRefuseMode] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [refusing, setRefusing] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);

  const handleRefuse = async () => {
    if (!refuseReason.trim() || refuseReason.trim().length < 5) {
      showToast(t('signDeclaration.refuseReasonRequired'), 'error');
      return;
    }
    setRefusing(true);
    try {
      await api.post(`/change-logs/${pending.id}/refuse`, { reason: refuseReason.trim() });
      showToast(t('signDeclaration.refuseSuccess'), 'success');
      if (onDone) onDone();
    } catch (err) {
      showToast(err.response?.data?.error || t('signDeclaration.refuseError'), 'error');
    } finally {
      setRefusing(false);
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const res = await api.get('/change-logs/pending-for-me');
      const mine = res.data.asEmployee || [];
      setPending(mine[0] || null);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  if (loading || !pending) return null;

  const labelForType = (type) =>
    type === 'NAME' ? t('signDeclaration.typeName') : t('signDeclaration.typeRoute');

  const formatNew = (log) => {
    if (log.changeType === 'NAME')
      return `${log.newValue.firstName || ''} ${log.newValue.lastName || ''}`.trim();
    const nv = log.newValue || {};
    const parts = [];
    if (nv.homeAddress) parts.push(`Domiciliu: ${nv.homeAddress}`);
    if (nv.workAddress) parts.push(`Loc muncă: ${nv.workAddress}`);
    if (nv.transportMode) parts.push(`Transport: ${nv.transportMode}`);
    if (nv.routeDuration) parts.push(`Durată: ${nv.routeDuration} min`);
    return parts.join(' · ');
  };
  const formatOld = (log) => {
    if (log.changeType === 'NAME')
      return `${log.oldValue.firstName || ''} ${log.oldValue.lastName || ''}`.trim();
    const ov = log.oldValue || {};
    const parts = [];
    if (ov.homeAddress) parts.push(`Domiciliu: ${ov.homeAddress}`);
    if (ov.workAddress) parts.push(`Loc muncă: ${ov.workAddress}`);
    if (ov.transportMode) parts.push(`Transport: ${ov.transportMode}`);
    if (ov.routeDuration) parts.push(`Durată: ${ov.routeDuration} min`);
    return parts.join(' · ') || '—';
  };

  const handleSign = () => {
    setShowPwdModal(true);
  };

  const submitSign = async (password) => {
    setSigning(true);
    try {
      const body = newSigData
        ? {
            newSignatureData: newSigData.signatureData,
            newSignatureType: newSigData.signatureType,
            newSignatureFont: newSigData.signatureFont,
            password,
          }
        : { password };
      await api.post(`/change-logs/${pending.id}/sign`, body);
      showToast(t('signDeclaration.signSuccess'), 'success');
      setShowPwdModal(false);
      if (onDone) onDone();
    } catch (err) {
      const data = err.response?.data;
      if (data?.requiresAdESActivation) {
        setShowPwdModal(false);
        showToast(data.error || t('signDeclaration.signError'), 'error');
        return;
      }
      throw err;
    } finally {
      setSigning(false);
    }
  };

  const handleUploadDoc = async () => {
    if (!docFile) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('supportingDoc', docFile);
      await api.post(`/change-logs/${pending.id}/upload-doc`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(t('signDeclaration.uploadSuccess'), 'success');
      setDocFile(null);
      await reload();
    } catch (err) {
      showToast(err.response?.data?.error || t('signDeclaration.uploadAttachError'), 'error');
    } finally {
      setUploadingDoc(false);
    }
  };

  const initiatorName = pending.initiator
    ? `${pending.initiator.firstName || ''} ${pending.initiator.lastName || ''}`.trim()
    : pending.initiatedBy === 'EMPLOYEE'
      ? t('signDeclaration.fallbackInitiator')
      : t('signDeclaration.fallbackInitiatorOther');

  // Pentru stil caligrafic la nouă semnătură: folosim noul nume din pending (nu numele curent)
  const newFullName =
    pending.changeType === 'NAME'
      ? `${pending.newValue?.firstName || ''} ${pending.newValue?.lastName || ''}`.trim()
      : null;

  if (showNewSigSetup) {
    return (
      <SignatureSetupModal
        isFirstTime={false}
        nameOverride={newFullName}
        onClose={() => setShowNewSigSetup(false)}
        onSuccess={() => {
          api
            .get('/signatures/my')
            .then((res) => {
              setNewSigData({
                signatureData: res.data.savedSignature,
                signatureType: res.data.signatureType,
                signatureFont: res.data.signatureFont,
              });
              setShowNewSigSetup(false);
              showToast(t('signDeclaration.newSignatureSavedToast'), 'success');
            })
            .catch(() => setShowNewSigSetup(false));
        }}
      />
    );
  }

  if (refuseMode) {
    return (
      <Modal
        open={true}
        onClose={() => {}}
        closable={false}
        title={t('signDeclaration.refuseTitle')}
        size="lg"
        footer={
          <div className="flex gap-3 justify-between w-full">
            <Button
              variant="secondary"
              onClick={() => {
                setRefuseMode(false);
                setRefuseReason('');
              }}
              disabled={refusing}
            >
              {t('signDeclaration.refuseBackBtn')}
            </Button>
            <Button
              variant="danger"
              onClick={handleRefuse}
              loading={refusing}
              disabled={refusing || refuseReason.trim().length < 5}
            >
              {t('signDeclaration.refuseConfirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <p>{t('signDeclaration.refuseDesc')}</p>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('signDeclaration.refuseReasonLabel')}
            </label>
            <textarea
              rows={5}
              value={refuseReason}
              onChange={(e) => setRefuseReason(e.target.value)}
              placeholder={t('signDeclaration.refuseReasonPh')}
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {refuseReason.trim().length}/5+ caractere
            </p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <>
      {showPwdModal && (
        <SignPasswordModal
          title={t('signDeclaration.signTitle') || 'Semnează declarația'}
          description={
            t('signDeclaration.signDesc') ||
            'Vei semna electronic această declarație cu semnătură electronică avansată conform Reg. UE 910/2014 (AdES).'
          }
          onSubmit={submitSign}
          onClose={() => setShowPwdModal(false)}
        />
      )}
      <Modal
        open={true}
        onClose={() => {}}
        closable={false}
        title={t('signDeclaration.title')}
        size="lg"
        footer={
          <div className="flex gap-3 justify-between w-full">
            <Button
              variant="outline"
              className="text-danger-600 border-danger-300 hover:bg-danger-50 dark:hover:bg-danger-900/30"
              onClick={() => {
                setRefuseReason('');
                setRefuseMode(true);
              }}
              disabled={signing}
            >
              {t('signDeclaration.refuseBtn')}
            </Button>
            <Button variant="primary" onClick={handleSign} loading={signing} disabled={signing}>
              {t('signDeclaration.signBtn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 text-sm text-slate-700 dark:text-slate-300">
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 rounded-md">
            <p className="font-semibold text-amber-800 dark:text-amber-300 mb-1">
              {t('signDeclaration.warningTitle')}
            </p>
            <p className="text-xs text-amber-700 dark:text-amber-300">
              {pending.initiatedBy === 'EMPLOYEE'
                ? t('signDeclaration.warningInitiated', {
                    name: initiatorName,
                    type: labelForType(pending.changeType),
                  })
                : t('signDeclaration.warningPrepared', {
                    name: initiatorName,
                    type: labelForType(pending.changeType),
                  })}{' '}
              {t('signDeclaration.warningBlock')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md">
              <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                {t('signDeclaration.previous')}
              </p>
              <p className="text-slate-700 dark:text-slate-300">{formatOld(pending)}</p>
            </div>
            <div className="bg-primary-50 dark:bg-primary-950/30 p-3 rounded-md border border-primary-200 dark:border-primary-800">
              <p className="text-xs uppercase tracking-wider text-primary-700 dark:text-primary-300 mb-1">
                {t('signDeclaration.proposed')}
              </p>
              <p className="text-slate-900 dark:text-slate-100 font-medium">{formatNew(pending)}</p>
            </div>
          </div>

          {pending.reason && (
            <p className="text-xs">
              <span className="font-medium">{t('signDeclaration.reason')}</span> {pending.reason}
            </p>
          )}

          <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-md text-xs">
            <p className="font-semibold mb-1">{t('signDeclaration.declarationText')}</p>
            <p className="italic">
              {t('signDeclaration.declarationBody', { date: formatDate(new Date()) })}
            </p>
          </div>

          <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              {t('signDeclaration.supportingDocLabel')}
            </p>
            {pending.supportingDocPath ? (
              <p className="text-xs text-accent-700 dark:text-accent-400">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    const token = localStorage.getItem('authToken');
                    const url = `${api.defaults.baseURL}/employees/${pending.employeeId}/change-logs/${pending.id}/document`;
                    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                      .then((r) => r.blob())
                      .then((b) => {
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(b);
                        a.download = pending.supportingDocName || 'document';
                        a.click();
                      })
                      .catch(() => showToast(t('signDeclaration.uploadError'), 'error'));
                  }}
                  className="text-primary-600 dark:text-primary-300 hover:underline"
                >
                  {t('signDeclaration.supportingDocAttached', {
                    name: pending.supportingDocName || 'document',
                  })}
                </a>
              </p>
            ) : (
              <>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                  {t('signDeclaration.supportingDocNone')}
                </p>
                <input
                  type="file"
                  accept=".pdf,image/jpeg,image/png,image/webp"
                  onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                  className="block w-full text-xs text-slate-700 dark:text-slate-300 file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:bg-primary-50 file:text-primary-700"
                />
                {docFile && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-slate-500">{docFile.name}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUploadDoc}
                      loading={uploadingDoc}
                      disabled={uploadingDoc}
                    >
                      {t('signDeclaration.uploadBtn')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {pending.changeType === 'NAME' && (
            <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                {t('signDeclaration.optionalSignatureTitle')}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                {t('signDeclaration.optionalSignatureDesc')}
              </p>
              {newSigData ? (
                <div className="flex items-center justify-between gap-3 bg-accent-50 dark:bg-accent-950/30 p-2 rounded">
                  <span className="text-xs text-accent-800 dark:text-accent-300">
                    {t('signDeclaration.newSignaturePrepared')}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewSigData(null)}
                      className="text-xs text-slate-600 hover:underline"
                    >
                      {t('signDeclaration.newSignatureDiscard')}
                    </button>
                    <button
                      onClick={() => {
                        setNewSigData(null);
                        setShowNewSigSetup(true);
                      }}
                      className="text-xs text-primary-600 hover:underline"
                    >
                      {t('signDeclaration.newSignatureChange')}
                    </button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" size="sm" onClick={() => setShowNewSigSetup(true)}>
                  {t('signDeclaration.changeSignatureBtn')}
                </Button>
              )}
            </div>
          )}

          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('signDeclaration.disagreeNote')}
          </p>
        </div>
      </Modal>
    </>
  );
};

export default ChangeSignaturePopup;
